import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { collectionsAPI, productsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Grid3x3, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export const CollectionsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_ids: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [collectionsRes, productsRes] = await Promise.all([
        collectionsAPI.getAll(),
        // Load all products for selection - images excluded by backend for performance
        productsAPI.getAll({ limit: 2000 })
      ]);
      setCollections(collectionsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCollection) {
        await collectionsAPI.update(editingCollection.id, formData);
        toast.success('Collection updated successfully');
      } else {
        await collectionsAPI.create(formData);
        toast.success('Collection created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ name: '', description: '', product_ids: [] });
      setEditingCollection(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save collection');
    }
  };

  const handleEdit = (collection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name,
      description: collection.description,
      product_ids: collection.product_ids,
    });
    setIsDialogOpen(true);
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consortium-green"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-barlow text-4xl font-bold text-slate-900 mb-2" data-testid="collections-title">
              Product Collections
            </h1>
            <p className="text-slate-600">Curated product bundles for client presentations</p>
          </div>
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-consortium-green hover:bg-consortium-green-hover gap-2"
                  onClick={() => {
                    setEditingCollection(null);
                    setFormData({ name: '', description: '', product_ids: [] });
                  }}
                  data-testid="create-collection-btn"
                >
                  <Plus className="w-4 h-4" />
                  Create Collection
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCollection ? 'Edit Collection' : 'Create New Collection'}</DialogTitle>
                  <DialogDescription>Group products into curated collections for easy client browsing</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="name">Collection Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Diwali Gifting 2026"
                        required
                        className="mt-2"
                        data-testid="collection-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe this collection..."
                        required
                        rows={3}
                        className="mt-2"
                        data-testid="collection-description-input"
                      />
                    </div>
                    <div>
                      <Label className="mb-3 block">Select Products ({formData.product_ids.length} selected)</Label>
                      <div className="border border-slate-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                        {products.map((product) => (
                          <div key={product.id} className="flex items-center space-x-3 py-2 border-b border-slate-100 last:border-0">
                            <Checkbox
                              id={`product-${product.id}`}
                              checked={formData.product_ids.includes(product.id)}
                              onCheckedChange={() => toggleProduct(product.id)}
                              data-testid={`select-product-${product.id}`}
                            />
                            <Label htmlFor={`product-${product.id}`} className="cursor-pointer flex-1">
                              <span className="font-medium">{product.name}</span>
                              <span className="text-sm text-slate-500 ml-2">({product.category})</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-consortium-green hover:bg-consortium-green-hover" data-testid="save-collection-btn">
                      {editingCollection ? 'Update Collection' : 'Create Collection'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {collections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collections.map((collection) => (
              <div
                key={collection.id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/collections/${collection.id}`)}
                data-testid={`collection-card-${collection.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-consortium-green-light rounded-lg flex items-center justify-center">
                    <Grid3x3 className="w-6 h-6 text-consortium-green" />
                  </div>
                  {isAdmin() && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(collection);
                      }}
                      data-testid={`edit-collection-${collection.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <h3 className="font-barlow font-semibold text-xl text-slate-900 mb-2">{collection.name}</h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{collection.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{collection.product_ids.length} products</span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Grid3x3 className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="font-barlow text-xl font-semibold text-slate-900 mb-2">No collections yet</h3>
            <p className="text-slate-600 mb-6">Create your first collection to group products</p>
            {isAdmin() && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="bg-consortium-green hover:bg-consortium-green-hover gap-2"
              >
                <Plus className="w-4 h-4" />
                Create First Collection
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
